import { Card } from '@startupquickstart/frontend';
import { Clock, Timer, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

import { EventLifecycleButtons, EventStatusBadge } from '@/components/events';
import {
  ConnectionBadge,
  type ConnectionStatus,
} from '@/components/events/live/ConnectionBadge';
import { SetTimeLimitControl } from '@/components/events/live/SetTimeLimitControl';
import type { Event, EventStatus } from '@/config/objects/events';

/** Tick the elapsed clock once a second while the event is running. */
const CLOCK_TICK_MS = 1_000;

/** Statuses where the hunt is mid-game and the live clocks should run. */
const RUNNING_STATUSES: readonly EventStatus[] = ['active', 'paused'];

/**
 * Formats elapsed milliseconds as h:mm:ss (or m:ss under an hour).
 */
function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/**
 * Ticking elapsed time since `startedAt`, frozen at `endedAt` when set.
 */
function useElapsedTime(startedAt: string | null | undefined, endedAt: string | null | undefined) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt || endedAt) return;
    const interval = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(interval);
  }, [startedAt, endedAt]);

  if (!startedAt) return null;
  const end = endedAt ? new Date(endedAt).getTime() : now;
  return formatElapsed(end - new Date(startedAt).getTime());
}

/**
 * Ticking countdown to `endTime` while the event is mid-game. Returns
 * `null` when there is no end time (open-ended hunt) or the event isn't
 * running — no countdown UI should render in either case.
 */
function useTimeRemaining(endTime: string | null | undefined, running: boolean) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!endTime || !running) return;
    const interval = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(interval);
  }, [endTime, running]);

  if (!endTime || !running) return null;
  return Math.max(0, new Date(endTime).getTime() - now);
}

export interface LiveStatusBarProps {
  event: Event;
  connectionStatus: ConnectionStatus;
}

/**
 * Command-center header strip: status badge, live event clock, team count,
 * socket connection state, and the lifecycle controls (pause/end/etc.)
 * so running the event never requires leaving the Live tab.
 *
 * @param props - Event under management and the socket connection status.
 */
export function LiveStatusBar({ event, connectionStatus }: LiveStatusBarProps) {
  const elapsed = useElapsedTime(event.startedAt, event.endedAt);
  const isRunning = RUNNING_STATUSES.includes(event.status);
  const remaining = useTimeRemaining(event.endTime, isRunning);

  return (
    <Card className="gap-0 py-0">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3">
        <EventStatusBadge status={event.status} />

        {/*
         * The elapsed count-up clock only reads meaningfully against a time
         * limit. Open-ended hunts (no `endTime`) have no elapsed-vs-limit
         * framing, so hide it entirely there — both the running "elapsed"
         * ticker and the "final" readout after the event ends.
         */}
        {event.endTime && elapsed && (
          <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="font-mono font-semibold tabular-nums">{elapsed}</span>
            <span className="text-muted-foreground">{event.endedAt ? 'final' : 'elapsed'}</span>
          </span>
        )}

        {remaining !== null && (
          <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
            <Timer className="h-4 w-4 text-muted-foreground" />
            <span className="font-mono font-semibold tabular-nums">{formatElapsed(remaining)}</span>
            <span className="text-muted-foreground">left</span>
          </span>
        )}

        {/* Open-ended hunt mid-game: let the facilitator start the clock. */}
        {isRunning && !event.endTime && <SetTimeLimitControl eventId={event.id} />}

        {event._count?.teams !== undefined && (
          <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold tabular-nums">{event._count.teams}</span>
            <span className="text-muted-foreground">teams</span>
          </span>
        )}

        <ConnectionBadge status={connectionStatus} />

        <div className="ml-auto flex items-center gap-2">
          <EventLifecycleButtons event={event} size="sm" className="flex items-center gap-2" />
        </div>
      </div>
    </Card>
  );
}
